import { X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useDemoMode } from "@/hooks/useDemoMode";
import { useAuthSession } from "@/lib/session-context";

/**
 * Persistent "Demo Mode – Sample Data" banner (AB#7529). Renders under the
 * topbar on every page while the caller's Demo Mode is on, so an advisor
 * sharing their screen can never mistake sample data for production.
 *
 * Carries an always-visible "Exit demo mode" affordance — mirroring present
 * mode's exit band — so the advisor can drop out of the demo from any page
 * instead of navigating back to the Energy Advisor landing toggle. `demo_mode`
 * being on implies `can_use_demo_mode` (same persona + tenant predicate), so
 * the turn-off is always permitted here.
 */
export function DemoModeBanner() {
  const { demo_mode } = useAuthSession();
  const demoMode = useDemoMode();
  if (!demo_mode) return null;
  return (
    <div
      role="status"
      className="relative flex items-center justify-center gap-2 border-b border-warning-mid bg-warning-dim px-4 py-1.5 text-xs font-medium text-warning"
    >
      Demo Mode – Sample Data
      <Button
        size="sm"
        onClick={() =>
          demoMode.mutate(false, {
            onError: () => toast.error("Couldn't turn off Demo Mode."),
          })
        }
        disabled={demoMode.isPending}
        className="absolute right-2 top-1/2 h-6 -translate-y-1/2 rounded-full bg-warning px-2.5 font-semibold text-warning-foreground hover:bg-warning/90"
      >
        <X size={13} strokeWidth={2.5} />
        {demoMode.isPending ? "Exiting…" : "Exit demo mode"}
      </Button>
    </div>
  );
}
